import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dataService, Category } from '../services/dataService';

export const useCategories = (type?: 'menuItem' | 'rawMaterial') => {
  const { tenantData } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCategories = useCallback(async () => {
    if (!tenantData?.id) return;

    try {
      setLoading(true);
      setError(null);
      const categoryList = await dataService.getCategories(tenantData.id, type);
      setCategories(categoryList);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, [tenantData?.id, type]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Initialize default categories if none exist
  const initializeDefaultCategories = useCallback(async () => {
    if (!tenantData?.id) return;

    try {
      await dataService.initializeDefaultCategories(tenantData.id);
      await loadCategories();
    } catch (err) {
      console.error('Error initializing categories:', err);
      setError('Failed to initialize default categories');
    }
  }, [tenantData?.id, loadCategories]);

  // Get category names for dropdowns
  const getCategoryNames = useCallback(() => {
    return categories.map(cat => cat.name);
  }, [categories]);

  // Get category names with "all" option for filters
  const getCategoryNamesWithAll = useCallback(() => {
    return ['all', ...categories.map(cat => cat.name)];
  }, [categories]);

  return {
    categories,
    loading,
    error,
    loadCategories,
    initializeDefaultCategories,
    getCategoryNames,
    getCategoryNamesWithAll
  };
}; 